import {
  createRequest,
  createPutRequest,
  createPostRequest,
  createStubRequest,
} from 'lp-redux-api'
import { getSessionToken } from 'auth'
import {
  getServicesAgreementSubmissionType,
  convertNullToEmptyString,
  removeAttributes,
} from 'utils'
import {
  ServicesAgreementSubmissionType,
  ServicesAgreementStatus,
} from 'config/automatic-rollovers'
import { AgreementsMapping, ProductAgreementType } from 'config/products'
import { isNotificationsAddonEnabled } from 'config/portal'

export const fetchUserProfile = createRequest('FETCH__USER_PROFILE', () => ({
  url: '/userProfiles',
  onSuccess: (response) => response[0],
  // camelizeResponse set to true due to the property "Retirement ServicesPortal Profile"
  camelizeResponse: true,
}))

export const updateLastLogin = createPutRequest('SET_LAST_LOGIN', () => ({
  url: '/userProfiles/logins',
  body: {
    loginDate: '',
  },
}))

export const reinitializeDocusign = createPutRequest(
  'REINITIALIZE_DOCUSIGN',
  (servicesAgreementId, productAgreementType, status) => {
    const config = {
      url: `/planAgreement/reInitializeDocusign/${servicesAgreementId}`,
    }
    if (
      productAgreementType === ProductAgreementType.BD &&
      status == ServicesAgreementStatus.PENDING_SIGNATURES
    ) {
      config.headers = {
        agreementType: productAgreementType,
      }
    }
    return config
  }
)

export const fetchServiceAgreements = createRequest(
  'FETCH__SERVICE_AGREEMENTS',
  () => ({
    url: '/user/serviceAgreements',
  })
)

export const fetchSearches = createRequest('FETCH__SEARCHES', () => ({
  url: '/searches',
}))

export const updateSearchOrder = createPutRequest(
  'UPDATE_SEARCH_ORDER',
  (body) => ({
    url: '/searchOrders',
    body,
  })
)

export const fetchOrderDetails = createRequest(
  'FETCH_ORDER_DETAILS',
  (orderId) => ({
    url: `/searchOrders/${orderId}`,
    onSuccess: (response) => {
      const searchOrder = response[0]
      return {
        orderId, // shim until API is updated to include value
        ...searchOrder,
      }
    },
  })
)

export const createUser = createPostRequest('CREATE_USER', (body) => ({
  url: '/users',
  body,
}))

export const editUser = createPutRequest('EDIT_USER', (contactId, body) => ({
  url: `/users/${contactId}`,
  body,
}))

export const updatePlanAgreement = createPutRequest(
  'UPDATE_PLAN_AGREEMENT',
  (agreementId, body) => ({
    url: `/planAgreements/${agreementId}`,
    body,
  })
)

export const fetchCompanyUsers = createRequest('FETCH_COMPANY_USERS', () => ({
  url: '/users',
}))

export const fetchPlanSponsors = createRequest('FETCH_PLAN_SPONSORS', () => ({
  url: '/planSponsors',
  // conversion of null values to empty string helps with initial values when passing into formik
  onSuccess: (res) => convertNullToEmptyString(res),
}))

export const fetchRecordkeepers = createRequest('FETCH_RECORDKEEPERS', () => ({
  url: '/recordKeepers',
  // conversion of null values to empty string helps with initial values when passing into formik
  onSuccess: (res) => convertNullToEmptyString(res),
}))

export const fetchTpas = createRequest('FETCH_TPAS', () => ({
  url: '/thirdPartyAdministrators',
  onSuccess: (res) => convertNullToEmptyString(res),
}))

export const fetchConsultants = createRequest('FETCH_CONSULTANTS', () => ({
  url: '/consultants',
  onSuccess: (res) => convertNullToEmptyString(res),
}))

export const fetchServicesAgreements = createRequest(
  'FETCH_SERVICES_AGREEMENTS',
  { url: '/planAgreements' }
)

export const fetchBdPlanAgreements = createRequest('FETCH_BD_PLAN_AGREEMENTS', {
  url: '/bdPlanAgreements',
})

export const fetchCompletedServicesAgreements = createRequest(
  'FETCH_COMPLETED_SERVICES_AGREEMENTS',
  (agreementType) => ({
    url: '/planAgreements',
    query:
      agreementType === AgreementsMapping.BD_PLAN_MATCHING
        ? {
            agreementStatus: 'Completed',
            addendumStatus: 'Completed',
            hasAddendum: false,
          }
        : agreementType === AgreementsMapping.BD_NEW_ADDENDUM
        ? {
            agreementStatus: 'Completed',
            hasAddendum: false,
          }
        : {
            agreementStatus: 'Completed',
          },
  })
)

export const createParticipantsData = createPostRequest(
  'CREATE_PARTICIPANT_DATA',
  ({ fileName, participants, summary, serviceType }) => {
    return {
      url: '/participantFile',
      body: {
        fileName,
        summary,
        participants: removeAttributes(participants, [
          'reasonForDiscard',
          'mappedPlanName',
        ]),
      },
      headers: {
        serviceType,
      },
    }
  }
)

export const createBDParticipantsData = createPostRequest(
  'CREATE_BD_PARTICIPANT_DATA',
  ({ fileName, participants, summary, contactID }) => {
    return {
      url: '/bdParticipantFile',
      body: {
        fileName,
        summary,
        contactID,
        participants: removeAttributes(participants, [
          'reasonForDiscard',
          'mappedPlanName',
        ]),
      },
    }
  }
)

export const createBDParticipantsDataByDeductFromDistribution =
  createPostRequest(
    'CREATE_BD_PARTICIPANT_DATA_BY_DEDUCT_FROM_DISTRIBUTION',
    ({ fileName, participants, summary, contactID }) => {
      return {
        url: '/bdDeductFromDistribution',
        body: {
          fileName,
          summary,
          contactID,
          participants: removeAttributes(participants, [
            'reasonForDiscard',
            'mappedPlanName',
          ]),
        },
      }
    }
  )

export const fetchAvailableInitialInvestments = createRequest(
  'FETCH_AVAILABLE_INITIAL_INVESTMENTS',
  () => ({
    url: '/availablePlanAgreementTypes',
  })
)

export const fetchServicesAgreementDetails = createRequest(
  'FETCH_SERVICES_AGREEMENT_DETAILS',
  (id) => ({
    url: '/planAgreement',
    query: { serviceAgreementId: id },
    onSuccess: (response) => response[0],
  })
)

export const submitServicesAgreementFile = createPostRequest(
  'SUBMIT_SERVICES_AGREEMENT_FILE',
  ({ planAgreementId, formData }) => ({
    url: '/submitPlanAgreementFile',
    body: formData,
    overrideHeaders: true,
    headers: {
      Authorization: `Bearer ${getSessionToken()}`,
      submissionType: planAgreementId
        ? ServicesAgreementSubmissionType.EXISTING_AGREEMENT
        : ServicesAgreementSubmissionType.NEW_AGREEMENT,
    },
  })
)
export const submitBDServicesAgreementFile = createPostRequest(
  'SUBMIT_SERVICES_AGREEMENT_FILE',
  ({ status, formData }) => ({
    url: '/bdSubmitPlanAgreementFile',
    body: formData,
    overrideHeaders: true,
    headers: {
      Authorization: `Bearer ${getSessionToken()}`,
      submissionType:
        status === ServicesAgreementStatus.PENDING_SIGNATURES
          ? ServicesAgreementSubmissionType.EXISTING_AGREEMENT
          : ServicesAgreementSubmissionType.NEW_AGREEMENT,
    },
  })
)

export const submitServicesAgreementForm = createPostRequest(
  'SUBMIT_SERVICES_AGREEMENT_FORM',
  ({
    planAgreementId,
    signingMethod,
    formData,
    productAgreementType,
    addendumOption,
    notificationsAddendumOption,
  }) => ({
    url: '/submitPlanAgreement',
    headers: {
      submissionType: getServicesAgreementSubmissionType(
        planAgreementId,
        signingMethod
      ),
      agreementType: productAgreementType,
      addendum: addendumOption,
      ...(isNotificationsAddonEnabled() && {
        notificationsAddendum: notificationsAddendumOption,
      }),
    },
    body: formData,
  })
)

export const submitSearchServicesAgreementForm = createPostRequest(
  'SUBMIT_SEARCH_SERVICES_AGREEMENT_FORM',
  (searchServicesAgreementData, productAgreementType) => ({
    url: '/submitPlanAgreement',
    headers: {
      submissionType: ServicesAgreementSubmissionType.SEARCH_SERVICES_AGREEMENT,
      agreementType: productAgreementType,
    },
    body: searchServicesAgreementData,
  })
)

export const submitAddendumServicesAgreementForm = createPutRequest(
  'SUBMIT_ADDENDUM_SERVICES_AGREEMNET_FORM',
  (data) => {
    const { portalAddendumType, ...body } = data
    return {
      url: '/bdAddendum',
      body,
      query: { addendumType: portalAddendumType },
    }
  }
)

export const fetchServicesAgreementParticipants = createRequest(
  'FETCH_SERVICES_AGREEMENT_PARTICIPANTS',
  (id) => ({
    url: `/participants/${id}`,
    onSuccess: (res) => ({
      servicesAgreementId: id,
      participants: res,
    }),
  })
)

export const fetchBenefitDistributionOrders = createRequest(
  'FETCH_BENEFIT_DISTRIBUTION_ORDERS',
  (id) => ({
    url: `/bdParticipants/${id}`,
    onSuccess: (response) => ({
      servicesAgreementId: id,
      orders: response,
    }),
  })
)

export const fetchCompanyOwner = createRequest('FETCH_COMPANY_OWNER', () => ({
  url: '/users/companyOwner',
}))

export const fetchErisapediaSearches = createRequest(
  'FETCH_ERISAPEDIA_SEARCHES',
  ({ name, ein }) => ({
    url: '/erisapediaSearch',
    query: {
      planName: name,
      ein,
    },
    onSuccess: (res) => convertNullToEmptyString(res),
  })
)

export const fetchPaymentMethod = createRequest('FETCH_PAYMENT_METHOD', () => ({
  url: `/paymentInfo`,
  onSuccess: (res) => (res.length >= 1 ? res[res.length - 1] : [{ mock: '' }]),
}))

export const deletePaymentMethod = createStubRequest(
  'DELETE_PAYMENT_METHOD',
  () => ({}),
  { delay: 2500 }
)

export const fetchSearchServices = createRequest(
  'FETCH_SEARCH_SERVICES',
  () => ({
    url: '/searchServices',
  })
)

export const generateSearchOrderCost = createPostRequest(
  'GENERATE_SEARCH_ORDER_COST',
  ({ searchService, numberOfSearches }) => ({
    url: '/searchServices/orderCost',
    body: { searchService, numberOfSearches },
  })
)

// Card Services

export const saveCompletePaymentDetails = createPostRequest(
  'SAVE_COMPLETE_PAYMENT_DETAILS',
  ({ paymentDetails }) => ({
    url: '/paymentInfo',
    body: paymentDetails,
  })
)

export const fetchCardToken = createRequest('FETCH_CARD_TOKEN', () => ({
  url: '/cardServices/token',
  camelizeResponse: true,
}))

export const updateSearchOrderDownloadDate = createPutRequest(
  'UPDATE_SEARCH_ORDER_DOWNLOAD_DATE',
  (searchOrderId) => ({
    url: `/searchOrders/${searchOrderId}/downloadedDates`,
    body: {},
    onSuccess: (res) => res,
  })
)

export const generateBDFeesCost = createPostRequest(
  'GENERATE_BD_FEES_COST',
  ({ participants }) => {
    return {
      url: '/bdFeeCalculation',
      body: {
        participants: participants,
      },
    }
  }
)
export const fetchNotificationsPlanAgreements = createRequest(
  'FETCH_NOTIFICATIONS_PLAN_AGREEMENTS',
  () => ({
    url: '/nsPlanAgreements',
  })
)
export const fetchNotificationsAgreements = createRequest(
  'FETCH__NOTIFICATIONS_AGREEMENTS',
  (agreementStep) => ({
    url: '/notificationPlanAgreements',
    query:
      agreementStep === AgreementsMapping.NOTIFICATIONS_PLAN_MATCHING
        ? {
            addendumStatus: 'Completed',
            agreementStatus: 'Completed',
          }
        : {
            agreementStatus: 'Completed',
          },
  })
)
export const generateNotificationsFeesCost = createPostRequest(
  'GENERATE_NOTIFICATIONS_FEES_COST',
  ({ participantsCount }) => {
    return {
      url: '/notificationServicesFeeCalculation',
      body: {
        totalParticipants: participantsCount,
      },
    }
  }
)
export const createNotificationServicesParticipantsData = createPostRequest(
  'CREATE_NOTIFICATION_SERVICES_PARTICIPANTS_DATA',
  ({ formData }) => {
    return {
      url: '/notificationParticipantFile',
      body: formData,
      overrideHeaders: true,
      headers: {
        Authorization: `Bearer ${getSessionToken()}`,
      },
    }
  }
)

export const fetchNotificationServicesAgreementParticipants = createRequest(
  'FETCH_NOTIFICATION_SERVICES_AGREEMENT_PARTICIPANTS',
  (id) => ({
    url: `/rspNsParticipantTable/${id}`,
    onSuccess: (res) => ({
      servicesAgreementId: id,
      participants: res,
    }),
  })
)

export const fetchRecordKeeperMaterials = createRequest(
  'FETCH_RECORDKEEPER_MATERIALS',
  () => ({
    url: '/recordKeeperMaterials',
  })
)

export const submitManualFileUpload = createPostRequest(
  'SUBMIT_MANUAL_FILE_UPLOAD',
  ({ formData }) => {
    return {
      url: '/manualFileUpload',
      body: formData,
      overrideHeaders: true,
      headers: {
        Authorization: `Bearer ${getSessionToken()}`,
      },
    }
  }
)
